import React, { useState, useCallback, useEffect } from 'react';

import { OETextInputSecure } from '../../atoms/oe-text-input-secure/oe-text-input-secure';
import FormFooter from '../../molecules/form-footer/form-footer';
import { mt } from '../../style-utils';

export interface CognitoChangePasswordFormLabels {
  passwordLabel: string;
  newPasswordLabel: string;
  newPasswordRepeatLabel: string;
  buttonCancel: string;
  buttonSubmit: string;
}

export interface CognitoChangePasswordFormProps {
  onSubmit: (password: string, newPassword: string) => void;
  onCancel: () => void;
  labels: CognitoChangePasswordFormLabels;
  isLoading?: boolean;
  error?: string;
  buttonColor?: string;
  buttonTextColor?: string;
}

export const CognitoChangePasswordForm: React.FC<
  CognitoChangePasswordFormProps
> = ({ labels, isLoading, error, onSubmit, onCancel, buttonTextColor }) => {
  const [newPasswordDoNotMatch, setPasswordDoNotMatch] = useState(false);
  const [formData, setformData] = useState({
    password: '',
    newPassword: '',
    newPasswordRepeat: '',
  });
  const { password, newPassword, newPasswordRepeat } = formData;

  useEffect(() => {
    if (newPasswordDoNotMatch && newPassword === newPasswordRepeat) {
      setPasswordDoNotMatch(false);
    }
  }, [newPasswordDoNotMatch, newPassword, newPasswordRepeat]);

  const handleFieldChange = (value: string, name?: string) => {
    if (name) {
      setformData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = useCallback(() => {
    if (newPassword !== newPasswordRepeat) {
      setPasswordDoNotMatch(true);
      return;
    }
    onSubmit(password, newPassword);
  }, [onSubmit, password, newPassword, newPasswordRepeat]);

  const newPasswordInputProps = {
    autoComplete: 'password' as const,
    textContentType: 'password' as const,
    style: mt(32),
    onChangeText: handleFieldChange,
    secure: true,
  };

  return (
    <>
      <OETextInputSecure
        label={labels.passwordLabel}
        name="password"
        value={password}
        {...newPasswordInputProps}
      />
      <OETextInputSecure
        label={labels.newPasswordLabel}
        name="newPassword"
        value={newPassword}
        {...newPasswordInputProps}
      />
      <OETextInputSecure
        label={labels.newPasswordRepeatLabel}
        name="newPasswordRepeat"
        value={newPasswordRepeat}
        error={newPasswordDoNotMatch}
        {...newPasswordInputProps}
      />
      <FormFooter
        buttonSubmit={{
          onPress: handleSubmit,
          loading: isLoading,
          disabled: !password || !newPassword || !newPasswordRepeat,
          label: labels.buttonSubmit,
          labelStyle: buttonTextColor ? { color: buttonTextColor } : {},
        }}
        buttonCancel={{
          onPress: onCancel,
          label: labels.buttonCancel,
          style: buttonTextColor ? { borderColor: buttonTextColor } : {},
          labelStyle: buttonTextColor ? { color: buttonTextColor } : {},
        }}
        error={error}
      />
    </>
  );
};

export default CognitoChangePasswordForm;
